import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { Button, IconLocked, tokens } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { Network } from '@fhs-legacy/frontend/src/utils/network/status';
import { getDateNowByTimezone, isRestaurantOpen } from '@fhs-legacy/frontend/src/utils/restaurant';
import { CART_PLACE_ORDER_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import { usePaymentMethodStore } from '../../../payment';
import { PaymentOrchestrator } from '../../../payment/core/payment-orchestrator';
import { useBuildPipelineContext } from '../../../payment/hooks/use-build-pipeline-context';
import type { PaymentPipelineError, PaymentPipelineResult } from '../../../payment/payment-types';
import { useCartStore } from '../../../state/cart-store';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';
import { MAX_TIP_AMOUNT_CENTS, useTipStore } from '../../../state/tip-store';
import { logCartPlaceOrder, logPaymentRetry } from '../../../utils/logging-utils';

import { getPlaceOrderButtonText } from './get-place-order-button-text';
import { handlePlaceOrder } from './handle-place-order';
import { ConfirmingOrderAlert, PaymentErrorAlert, PricingAlert } from './payment-alerts';

export default function PlaceOrderButton() {
  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentError, setCurrentError] = useState<PaymentPipelineError | null>(null);
  const [pendingCommitRetry, setPendingCommitRetry] = useState(false);
  const lastResultRef = useRef<PaymentPipelineResult | null>(null);
  const orchestratorRef = useRef<PaymentOrchestrator>(new PaymentOrchestrator());
  const pricingStartedRef = useRef(false);
  const pressInFlightRef = useRef(false);
  const [isOfflineDuringCommit, setIsOfflineDuringCommit] = useState(false);

  // Store hooks
  const cartEntries = useCartStore(s => s.cartEntries);
  const isPaymentReady = usePaymentMethodStore(state => state.isPaymentReady());
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const userAccountsLoadError = usePaymentMethodStore(state => state.userAccountsLoadError);
  const setUserAccountsLoadError = usePaymentMethodStore(state => state.setUserAccountsLoadError);
  // Track the previous payment method to detect when user changes it
  const prevPaymentMethodRef = useRef(selectedPaymentMethod);
  const otherTipValue = useTipStore(state => state.otherTipValue);
  const selectedTip = useTipStore(state => state.selectedTip);
  const { buildPipelineContext } = useBuildPipelineContext();
  const { setShouldSkipPriceOrder, serverOrderLoading, serverOrderError, serverOrder, tryPricing } =
    useCartContext();
  const { isDelivery } = useOrderContext();
  const { store, fetchStore } = useStoreContext();
  const { isOfferValidationError } = useLoyaltyContext();
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { rewardTierApplied } = useRewardTierApplied();

  const intl = useIntl();
  const hasReachedMaxRedemptionLimit = useAppSelector(
    selectors.loyalty.createHasReachedMaxRedemptionLimitSelector(intl)
  );

  const currentState = usePaymentFlowStore(state => state.currentState);
  const retryInfo = usePaymentFlowStore(state => state.retryInfo);
  const setRbiOrderId = usePaymentFlowStore(state => state.setRbiOrderId);
  const setCurrentState = usePaymentFlowStore(state => state.setCurrentState);
  const setRetryInfo = usePaymentFlowStore(state => state.setRetryInfo);
  const { logRBIEvent } = useCRMEventsContext();

  // All Button States
  const serverOrderProcessing =
    serverOrderLoading ||
    serverOrder?.status === 'PRICE_REQUESTED' ||
    serverOrder?.status === 'INSERT_REQUESTED' ||
    // @ts-ignore   not yet generated in type
    serverOrder?.status === 'FIRE_REQUESTED' ||
    (serverOrderLoading && serverOrder?.status !== 'INSERT_ERROR'); // STOP INFINITE LOADING ON ERROR

  // Pipeline is processing when currentState indicates active processing
  // (e.g., when handlePlaceOrder is called from gift card reload action sheet)
  const isPipelineProcessing =
    currentState !== 'idle' && currentState !== 'success' && currentState !== 'error';

  const hasCurrentPrice = serverOrder?.status === 'PRICE_SUCCESSFUL';
  const isDeliveryFeesLoading = isDelivery && !serverOrder?.delivery;
  const isButtonLoading =
    isProcessing ||
    serverOrderProcessing ||
    isPipelineProcessing ||
    offersLoading ||
    isDeliveryFeesLoading;

  const isConfirmingSubmittedOrder =
    currentState === 'confirming' ||
    (currentState === 'committing' && isOfflineDuringCommit) ||
    (serverOrder?.status === 'INSERT_REQUESTED' && (currentState === 'error' || !!currentError));

  const isButtonDisabled =
    isOfferValidationError ||
    !isPaymentReady ||
    isProcessing ||
    serverOrderProcessing ||
    isPipelineProcessing ||
    isDeliveryFeesLoading ||
    !hasCurrentPrice ||
    !cartEntries.length ||
    (!!serverOrderError && !isConfirmingSubmittedOrder) ||
    !!(hasReachedMaxRedemptionLimit && rewardTierApplied) ||
    (selectedTip === 'other' && Number(otherTipValue) > MAX_TIP_AMOUNT_CENTS);

  const buttonText = getPlaceOrderButtonText(
    currentState === 'committing' && isOfflineDuringCommit ? 'confirming' : currentState,
    !!retryInfo,
    serverOrderLoading || isDeliveryFeesLoading,
    serverOrder?.status
  );

  // Clear payment error when user changes payment method
  // When Google Pay (or Apple Pay) fails, the error shows and payment method stays the same.
  // When user selects a different payment method, the error clears.
  useEffect(() => {
    if (selectedPaymentMethod !== prevPaymentMethodRef.current) {
      // Payment method changed - clear any existing error
      if (currentError) {
        setCurrentError(null);
        lastResultRef.current = null;
      }
      if (userAccountsLoadError) {
        setUserAccountsLoadError(null);
      }
      prevPaymentMethodRef.current = selectedPaymentMethod;
    }
  }, [selectedPaymentMethod, currentError, userAccountsLoadError, setUserAccountsLoadError]);

  useEffect(() => {
    if (currentState !== 'committing' && currentState !== 'confirming') {
      setIsOfflineDuringCommit(false);
      return;
    }

    const listener = Network.addListener(status => {
      setIsOfflineDuringCommit(!status.connected);
    });
    return () => listener.remove();
  }, [currentState]);

  const placeOrder = useCallback(async () => {
    try {
      await handlePlaceOrder({
        lastResultRef,
        orchestratorRef,
        buildPipelineContext,
        setIsProcessing,
        setShouldSkipPriceOrder,
        setCurrentError,
        logRBIEvent,
      });
    } finally {
      pressInFlightRef.current = false;
    }
  }, [buildPipelineContext, setShouldSkipPriceOrder, logRBIEvent]);

  // After a COMMIT_ORDER_FAILED retry, wait for repricing to complete before retrying the pipeline.
  // Uses pricingStartedRef to avoid firing immediately when serverOrder.status is already
  // PRICE_SUCCESSFUL from the previous (pre-failure) pricing cycle.
  useEffect(() => {
    if (!pendingCommitRetry) {
      pricingStartedRef.current = false;
      return;
    }

    if (serverOrderLoading || serverOrder?.status === 'PRICE_REQUESTED') {
      pricingStartedRef.current = true;
      return;
    }

    if (!pricingStartedRef.current) {
      return;
    }

    setPendingCommitRetry(false);

    if (serverOrder?.status === 'PRICE_SUCCESSFUL') {
      void placeOrder();
    } else {
      setIsProcessing(false);
      pressInFlightRef.current = false;
    }
  }, [pendingCommitRetry, serverOrderLoading, serverOrder?.status, placeOrder]);

  // After an ambiguous commit the pipeline may have landed on 'error' while the order poll still
  // has (or later receives) INSERT_*. Promote to success/error from that status so we don't depend
  // on a second recovery poller in this component.
  useEffect(() => {
    if (currentState === 'confirming') {
      return;
    }

    if (
      serverOrder?.status === 'INSERT_SUCCESSFUL' &&
      (currentState === 'insertRequested' || currentState === 'error' || isProcessing)
    ) {
      setCurrentError(null);
      setRetryInfo(null);
      setCurrentState('success');
      return;
    }

    if (currentState === 'insertRequested' && serverOrder?.status === 'INSERT_ERROR') {
      setIsProcessing(false);
      setCurrentState('error');
    }
  }, [currentState, serverOrder?.status, isProcessing, setCurrentState, setRetryInfo]);

  const handleClearError = () => {
    setCurrentError(null);
    lastResultRef.current = null;
  };

  const handleClearUserAccountsLoadError = () => {
    setUserAccountsLoadError(null);
  };

  const userAccountsLoadPipelineError: PaymentPipelineError | null = userAccountsLoadError
    ? {
        code: 'USER_ACCOUNTS_LOAD_FAILED',
        message: userAccountsLoadError,
        userMessage: userAccountsLoadError,
      }
    : null;

  const handlePress = async () => {
    if (pressInFlightRef.current) {
      return;
    }
    pressInFlightRef.current = true;

    if (isDelivery && store?.number) {
      setIsProcessing(true);
      setCurrentError(null);
      lastResultRef.current = null;

      const fetchedStore = await fetchStore(store.number);
      if (
        fetchedStore &&
        !isRestaurantOpen(
          fetchedStore.deliveryHours,
          getDateNowByTimezone({ timeZone: fetchedStore.timezone || undefined })
        )
      ) {
        setIsProcessing(false);
        pressInFlightRef.current = false;
        return;
      }
    }

    if (retryInfo) {
      logPaymentRetry(
        logRBIEvent,
        `after failed ${retryInfo.failedStep}. Retrying from ${retryInfo.retryFromStep}`
      );

      if (retryInfo.errorCode === 'COMMIT_ORDER_FAILED') {
        if (serverOrder?.rbiOrderId) {
          setRbiOrderId(serverOrder.rbiOrderId);
        }
        setIsProcessing(true);
        tryPricing();
        setPendingCommitRetry(true);
        return;
      }
    } else {
      logCartPlaceOrder(logRBIEvent);
    }

    // set rbiOrderId for routing off cart or checkout page
    if (serverOrder?.rbiOrderId) {
      setRbiOrderId(serverOrder.rbiOrderId);
    }

    void placeOrder();
  };

  // Hold pricing/payment errors while INSERT_REQUESTED — the poll is still confirming the order.
  const showPricingAlert = !!serverOrderError && !isPipelineProcessing && !isConfirmingSubmittedOrder;
  const showPaymentError = !!currentError && !isConfirmingSubmittedOrder;

  return (
    <View style={[styles.container, styles.buttonContainer]}>
      {isConfirmingSubmittedOrder && <ConfirmingOrderAlert />}
      {showPricingAlert && <PricingAlert />}
      {userAccountsLoadPipelineError && (
        <PaymentErrorAlert
          error={userAccountsLoadPipelineError}
          onClear={handleClearUserAccountsLoadError}
        />
      )}
      {showPaymentError && <PaymentErrorAlert error={currentError} onClear={handleClearError} />}

      <Button
        testID={CART_PLACE_ORDER_BUTTON}
        size="lg"
        loading={isButtonLoading}
        disabled={isButtonDisabled}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={isButtonLoading ? 'Processing order' : buttonText}
        accessibilityHint={
          isButtonDisabled
            ? serverOrderError
              ? 'Order cannot be placed due to a pricing error. Please try again'
              : isDeliveryFeesLoading
                ? 'Please wait while delivery fees are calculated'
                : 'Please select a payment method and ensure your cart is not empty'
            : 'Places your order and processes payment'
        }
        accessibilityState={{ disabled: isButtonDisabled, busy: isButtonLoading }}
      >
        {!isButtonLoading && (
          <Button.Icon>
            <IconLocked />
          </Button.Icon>
        )}
        <Button.Text>{buttonText}</Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  container: {
    marginHorizontal: 'auto',
    padding: 16,
    width: '100%',
  },
});
